// src/components/Base/BasePaper.tsx
import React from "react";
import { Paper, PaperProps, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { RoundIconButton } from "components/Base/Buttons";

export interface BasePaperProps extends PaperProps {
  onClose?: () => void;

  /**
   * If true, paper dims itself (50% opacity + disabled pointer events)
   */
  dimmed?: boolean;

  /**
   * Optional override opacity (0–1)
   */
  opacity?: number;

  /**
   * Optional override pointer events
   */
  disabledInteraction?: boolean;

  children: React.ReactNode;
}

export default function BasePaper({
  onClose,
  dimmed = false,
  opacity,
  disabledInteraction,
  children,
  sx,
  elevation = 3,
  ...rest
}: BasePaperProps) {
  const resolvedOpacity = opacity !== undefined ? opacity : dimmed ? 0.5 : 1;

  const pointerEvents =
    disabledInteraction !== undefined
      ? disabledInteraction
        ? "none"
        : "auto"
      : dimmed
      ? "none"
      : "auto";

  return (
    <Paper
      elevation={elevation}
      sx={{
        p: 3,
        mx: 2,
        my: 2,
        borderRadius: 3,
        position: "relative",
        opacity: resolvedOpacity,
        pointerEvents,
        transition: "opacity 0.2s ease",
        ...sx,
      }}
      {...rest}
    >
      {onClose && (
        <RoundIconButton
          colour="clear"
          displaySize="inline"
          icon={<CloseIcon fontSize="small" />}
          onClick={onClose}
          iconColor={(theme) => theme.custom.base.neutral[600].dark}
          hoverIconColor={(theme) => theme.custom.functional.error.dark}
          hoverBackgroundColor="transparent"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        />
      )}

      <Box>{children}</Box>
    </Paper>
  );
}